import React, { useState, useEffect, useMemo } from 'react';
import { useLandlordStore, Invoice } from '@/stores/useLandlordStore';
import { Download, Filter, Calendar, BarChart2, DollarSign, Activity } from 'lucide-react';
import { motion } from 'framer-motion';
import { useToast } from '@/contexts/ToastContext';

export const LandlordReport: React.FC = () => {
    const { invoices, properties, units, fetchInvoices, fetchProperties, fetchUnits, loading } = useLandlordStore();
    const { warning, success } = useToast();

    // Active filters
    const [selectedPropertyId, setSelectedPropertyId] = useState<string>('all');
    const [selectedUnitId, setSelectedUnitId] = useState<string>('all');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    // Fetch initial data
    useEffect(() => {
        fetchProperties();
        fetchInvoices();
    }, []);

    // Fetch units when property changes
    useEffect(() => {
        if (selectedPropertyId !== 'all') {
            fetchUnits(selectedPropertyId);
        }
        // Reset unit if property changes
        setSelectedUnitId('all');
    }, [selectedPropertyId]);

    // Derived filtered data
    const filteredInvoices = useMemo(() => {
        return invoices.filter(inv => {
            // Property filter
            if (selectedPropertyId !== 'all' && inv.property_id !== selectedPropertyId) return false;

            // Unit filter (Requires finding the unit number to match the invoice)
            if (selectedUnitId !== 'all') {
                const targetUnit = units.find(u => u.id === selectedUnitId);
                if (targetUnit && inv.unit_number !== targetUnit.unit_number) return false;
            }

            // Date filter
            if (startDate) {
                const invDate = new Date(inv.created_at || inv.due_date);
                if (invDate < new Date(startDate)) return false;
            }
            if (endDate) {
                const invDate = new Date(inv.created_at || inv.due_date);
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999); // Include entire end day
                if (invDate > end) return false;
            }

            return true;
        });
    }, [invoices, selectedPropertyId, selectedUnitId, startDate, endDate, units]);

    // Analytics calculated on the fly
    const totalBilled = filteredInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);
    const totalCollected = filteredInvoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0);
    const outstandingBalance = filteredInvoices.reduce((sum, inv) => sum + (inv.balance || 0), 0);
    const collectionRate = totalBilled > 0 ? ((totalCollected / totalBilled) * 100).toFixed(1) : '0.0';

    const handleDownloadReport = () => {
        if (filteredInvoices.length === 0) {
            warning('No data', 'No data available to export for the selected filters.');
            return;
        }

        // CSV Headers
        const headers = ['Invoice ID', 'Date', 'Due Date', 'Property', 'Unit', 'Tenant', 'Type', 'Status', 'Billed (KES)', 'Paid (KES)', 'Balance (KES)'];

        // Map data to CSV rows
        const rows = filteredInvoices.map(inv => [
            inv.id,
            new Date(inv.created_at).toLocaleDateString(),
            new Date(inv.due_date).toLocaleDateString(),
            inv.property_name || 'N/A',
            inv.unit_number || 'N/A',
            inv.tenant_name || 'N/A',
            inv.type || 'N/A',
            inv.status || 'N/A',
            inv.amount || 0,
            inv.amount_paid || 0,
            inv.balance || 0
        ]);

        // Combine and escape CSV fields properly
        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        // Create Blob and download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        const timestamp = new Date().toISOString().split('T')[0];
        link.setAttribute('href', url);
        link.setAttribute('download', `financial_report_${timestamp}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        success('Report downloaded', `${filteredInvoices.length} transactions exported to CSV.`);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">Financial Reports</h1>
                    <p className="text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle text-sm mt-1">Generate dynamic views of your income and export directly to Excel.</p>
                </div>

                <button
                    onClick={handleDownloadReport}
                    disabled={filteredInvoices.length === 0}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                >
                    <Download size={20} />
                    Download CSV Report
                </button>
            </div>

            {/* Dynamic Analytics Banner */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg">
                            <BarChart2 size={24} />
                        </div>
                    </div>
                    <p className="text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle mb-1 text-sm font-medium">Total Billed</p>
                    <h3 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">KES {totalBilled.toLocaleString()}</h3>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-green-100 dark:bg-green-900/30 text-green-600 rounded-lg">
                            <DollarSign size={24} />
                        </div>
                    </div>
                    <p className="text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle mb-1 text-sm font-medium">Total Collected</p>
                    <h3 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">KES {totalCollected.toLocaleString()}</h3>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-red-100 dark:bg-red-900/30 text-danger dark:text-danger-dark rounded-lg">
                            <Activity size={24} />
                        </div>
                    </div>
                    <p className="text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle mb-1 text-sm font-medium">Outstanding Balance</p>
                    <h3 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">KES {outstandingBalance.toLocaleString()}</h3>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 rounded-lg">
                            <Filter size={24} />
                        </div>
                        <span className="text-sm font-medium text-indigo-600 bg-indigo-50 dark:bg-indigo-900/40 px-2 py-1 rounded-full">Report Scope</span>
                    </div>
                    <p className="text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle mb-1 text-sm font-medium">Collection Rate</p>
                    <h3 className="text-2xl font-bold text-light-text dark:text-dark-text dark:text-white">{collectionRate}%</h3>
                </motion.div>
            </div>

            {/* Report Builder Controls */}
            <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
                <h3 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white mb-6">Report Parameters</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Property Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Select Property
                        </label>
                        <select
                            value={selectedPropertyId}
                            onChange={(e) => setSelectedPropertyId(e.target.value)}
                            className="w-full px-4 py-2.5 bg-light-bg dark:bg-dark-bg dark:bg-gray-900 border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text dark:text-white transition-all"
                        >
                            <option value="all">Entire Portfolio (All Properties)</option>
                            {properties.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Unit Filter */}
                    <div className={`${selectedPropertyId === 'all' ? 'opacity-50 pointer-events-none' : ''}`}>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Select Unit
                        </label>
                        <select
                            value={selectedUnitId}
                            onChange={(e) => setSelectedUnitId(e.target.value)}
                            disabled={selectedPropertyId === 'all'}
                            className="w-full px-4 py-2.5 bg-light-bg dark:bg-dark-bg dark:bg-gray-900 border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text dark:text-white transition-all disabled:bg-gray-100 dark:bg-gray-100 dark:bg-gray-800 disabled:text-light-subtle dark:text-dark-subtle"
                        >
                            <option value="all">All Units in Property</option>
                            {units.map(u => (
                                <option key={u.id} value={u.id}>Unit {u.unit_number}</option>
                            ))}
                        </select>
                        {selectedPropertyId === 'all' && (
                            <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">Select a property first.</p>
                        )}
                    </div>

                    {/* Start Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                            <Calendar size={14} className="text-light-subtle dark:text-dark-subtle" /> Start Date
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-4 py-2.5 bg-light-bg dark:bg-dark-bg dark:bg-gray-900 border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text dark:text-white transition-all"
                        />
                    </div>

                    {/* End Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                            <Calendar size={14} className="text-light-subtle dark:text-dark-subtle" /> End Date
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-4 py-2.5 bg-light-bg dark:bg-dark-bg dark:bg-gray-900 border border-light-border dark:border-dark-border dark:border-light-border dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none text-light-text dark:text-dark-text dark:text-white transition-all"
                        />
                    </div>
                </div>
            </div>

            {/* Data Preview */}
            <div className="bg-light-card dark:bg-dark-card dark:bg-dark-card rounded-xl shadow-sm border border-light-border dark:border-dark-border overflow-hidden">
                <div className="flex justify-between items-center p-6 border-b border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg/50 dark:bg-gray-100 dark:bg-gray-800/20">
                    <h3 className="text-lg font-bold text-light-text dark:text-dark-text dark:text-white">
                        Report Preview <span className="text-sm font-normal text-light-subtle dark:text-dark-subtle ml-2">({filteredInvoices.length} transactions match filters)</span>
                    </h3>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-light-bg dark:bg-dark-bg dark:bg-gray-100 dark:bg-gray-800 text-xs uppercase text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle">
                            <tr>
                                <th className="px-6 py-4 font-medium">Invoice ID</th>
                                <th className="px-6 py-4 font-medium">Date</th>
                                <th className="px-6 py-4 font-medium">Property & Unit</th>
                                <th className="px-6 py-4 font-medium">Billed</th>
                                <th className="px-6 py-4 font-medium">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-light-border dark:divide-dark-border dark:divide-light-border dark:divide-dark-border">
                            {loading && invoices.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-light-subtle dark:text-dark-subtle">Loading data...</td>
                                </tr>
                            ) : filteredInvoices.length > 0 ? (
                                // Show preview of up to 5 items
                                filteredInvoices.slice(0, 5).map(inv => (
                                    <tr key={inv.id} className="hover:bg-light-bg dark:bg-dark-bg dark:hover:bg-gray-100 dark:bg-gray-800/50 transition-colors">
                                        <td className="px-6 py-4 text-sm text-light-text dark:text-dark-text dark:text-white font-medium">
                                            {inv.id.substring(0, 8)}...
                                        </td>
                                        <td className="px-6 py-4 text-sm text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle">
                                            {new Date(inv.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-light-text dark:text-dark-text dark:text-white">
                                            {inv.property_name || 'N/A'}<br />
                                            <span className="text-xs text-light-subtle dark:text-dark-subtle">Unit {inv.unit_number || 'N/A'}</span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-light-text dark:text-dark-text dark:text-white">
                                            KES {inv.amount?.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${inv.status === 'Paid' ? 'bg-green-100 text-green-700' :
                                                    inv.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                                                        'bg-red-100 text-red-700'
                                                }`}>
                                                {inv.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-light-subtle dark:text-dark-subtle dark:text-light-subtle dark:text-dark-subtle">
                                        No financial records found for the selected configuration.
                                    </td>
                                </tr>
                            )}
                            {filteredInvoices.length > 5 && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-4 text-center text-sm font-medium text-primary bg-primary/5">
                                        + {filteredInvoices.length - 5} more transactions will be included in the export
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
